import { useEffect, useState, useCallback } from "react";
import socket from "../socket/socket";
import FinalSubmissionDialog from "./popups/FinalSubmissionDialog";
import { useTimer } from "../context/TimerContext";
import { examStartApiSubject } from "../services/service";
import { decryptData } from "../crypto";
import { useAttemptedQuestions } from "../context/AttemptedQuestionsContext";
import { useQuestion } from "../context/QuestionsContext";
import { CircularProgress } from "@mui/material";

export let leftTime = "";
export const changeSubjectTimeUtil = (activeSubjectId, getNext = true) => {
  const subjectDurations = JSON.parse(
    sessionStorage.getItem("subjectduration") || "[]"
  );
  const index =
    subjectDurations?.findIndex(
      (time) => time?.subject_id === activeSubjectId
    ) ?? -1;

  if (index >= 0) {
    const activeIndex = getNext
      ? (subjectDurations.length > index + 1 ? index + 1 : index)
      : index;
    const durationSeconds = subjectDurations[activeIndex]?.duration * 60;
    const newTime = secondsToTimeString(durationSeconds);
    sessionStorage.setItem("remainingTime", newTime);
    sessionStorage.setItem("examDuration", newTime);
    return newTime;
  }
  return "00:00:00";
};

// Utility functions outside component
function timeStringToSeconds(timeString) {
  if (!timeString || timeString === "00:00:00") return 0;
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

function secondsToTimeString(totalSeconds) {
  if (totalSeconds <= 0) return "00:00:00";
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    "0"
  );
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

function decrementTime(timeString, setIsTimeUp, setIsOnSubmit) {
  if (sessionStorage.getItem("isTimeUp")) {
    if (decryptData(sessionStorage.getItem("exam_template")) === "ipmat") {
      sessionStorage.setItem("isOnsubmit", true);
      setIsOnSubmit(true);
    }
    setIsTimeUp(true);
    return "00:00:00";
  }

  let totalSeconds = timeString.split(":").reduce((acc, time, index) => {
    return acc + Number(time) * [3600, 60, 1][index];
  }, 0);

  // if (totalSeconds <= 1) return "00:00:00";
  if (totalSeconds <= 1) {
    sessionStorage.setItem("isTimeUp", true);
    if (decryptData(sessionStorage.getItem("exam_template")) === "ipmat") {
      sessionStorage.setItem("isOnsubmit", true);
      setIsOnSubmit(true);
    }
    setIsTimeUp(true);
    return "00:00:00";
  }

  totalSeconds -= 1;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    String(hours).padStart(2, "0"),
    String(minutes).padStart(2, "0"),
    String(seconds).padStart(2, "0"),
  ].join(":");
}

function TimerComponentSubject({
  setIsTimeUp,
  showSubmitModal,
  handleCloseSubmitModal,
  handleFinalSubmit,
  isTimeUp,
  stats,
  submitError,
  countdownTime,
  finalSubmitChecked,
  handleFinalSubmitCheck,
  activeSubjectId,
  nextSubjectCounter,
  nextSubject,
  isSubjectSubmit,
  subjectNames,
  isSubjectTimeUp,
  setIsSubjectTimeUp,
  setIsOnSubmit,
  onInitialized = () => {},
}) {
  // const [remainingTime, setRemainingTime] = useState("00:00:00");
  const { remainingTime, setRemainingTime } = useTimer();
  const { question, loading: questionLoading } = useQuestion();
  const { attemptedLoader } = useAttemptedQuestions();
  const [extraTime, setExtraTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentSubjectDuration, setCurrentSubjectDuration] = useState(0);

  // Get subject duration safely
  const getSubjectDuration = useCallback(() => {
    try {
      const durations = JSON.parse(
        sessionStorage.getItem("subjectduration") || "[]"
      );

      const index =
        durations?.findIndex((time) => time?.subject_id === activeSubjectId) ??
        -1;

      const duration = index >= 0 ? durations[index]?.duration * 60 : 0;
      setCurrentSubjectDuration(duration); // Store current subject duration
      return duration;
    } catch (error) {
      console.error("Error parsing subject durations:", error);
      return 0;
    }
  }, [activeSubjectId]);

  // Initialize timer
  const initializeTimer = useCallback(() => {
    try {
      const durationSeconds = getSubjectDuration();
      const defaultTime = secondsToTimeString(durationSeconds);
      const storedTime = sessionStorage.getItem("remainingTime");

      const initialTime =
        storedTime && storedTime !== "00:00:00" ? storedTime : defaultTime;

      setRemainingTime(initialTime);
      // sessionStorage.setItem("remainingTime", initialTime);
      sessionStorage.setItem("examDuration", defaultTime);
      setIsTransitioning(false); // Reset transitioning state
    } catch (error) {
      console.error("Timer initialization failed:", error);
    }
  }, [getSubjectDuration]);

  // Handle subject change
  const changeSubjectTime = () => {
    nextSubject();
    const newTime = changeSubjectTimeUtil(activeSubjectId);
    setRemainingTime(newTime);
    setIsTransitioning(false);
  };

  // Apply extra time
  const applyExtraTime = useCallback(() => {
    if (!extraTime) return;

    const durationSeconds = getSubjectDuration();
    const currentSeconds = timeStringToSeconds(remainingTime);
    const extraSeconds = Number(extraTime);
    const totalSeconds = currentSeconds + extraSeconds;

    const newTime = secondsToTimeString(
      Math.min(totalSeconds, durationSeconds)
    );

    sessionStorage.setItem("remainingTime", newTime);
    setRemainingTime(newTime);
    setExtraTime(null);
  }, [extraTime, remainingTime, getSubjectDuration]);

  // Initialize on mount and subject change
  useEffect(() => {
    if (!questionLoading && !attemptedLoader && activeSubjectId) {
      initializeTimer();
    }
  }, [initializeTimer, questionLoading, attemptedLoader, activeSubjectId]);

  const start = async () => {
    const examId = sessionStorage.getItem("examId");

    // Check if subject is already submitted
    const submittedSubjects = JSON.parse(
      sessionStorage.getItem("submitted_subjects") || "[]"
    );

    if (submittedSubjects.includes(String(activeSubjectId))) {
      console.log(`Subject ${activeSubjectId} already submitted, skipping...`);
      setIsInitialized(true);
      return;
    }

    // Check if timer is already initialized for this session
    const hasInitializedTimer = sessionStorage.getItem(
      `timerInitialized_${activeSubjectId}`
    );

    try {
      const res = await examStartApiSubject(examId);

      if (res.subject_wise_time_left?.length > 0 && !hasInitializedTimer) {
        const subjectTime = res.subject_wise_time_left.find(
          (time) => Number(time.subject_id) === Number(activeSubjectId)
        );

        if (subjectTime) {
          sessionStorage.setItem("remainingTime", subjectTime.time_left);
          sessionStorage.setItem(`timerInitialized_${activeSubjectId}`, "true");
          setRemainingTime(subjectTime.time_left);
          console.log(
            `Timer initialized for subject ${activeSubjectId}: ${subjectTime.time_left}`
          );
        }
      }
      setIsInitialized(true);
    } catch (error) {
      console.error("Error starting exam:", error);
      setIsInitialized(true);
      // Consider adding error handling/retry logic here
    }
  };

  useEffect(() => {
    if (!questionLoading && !attemptedLoader && activeSubjectId) {
      start();
    }
  }, [activeSubjectId, questionLoading, attemptedLoader]);

  // Socket setup
  useEffect(() => {
    if (!isInitialized) return;

    const user = sessionStorage.getItem("userId");
    const exam = sessionStorage.getItem("examId");

    const handleExtraTime = ({ examId, userIds, extraTime }) => {
      if (
        userIds.map(String).includes(user) &&
        Number(exam) === Number(examId)
      ) {
        const storeTime = Number(sessionStorage.getItem("extraTime") || 0);
        sessionStorage.setItem("extraTime", storeTime + Number(extraTime));
        setExtraTime(extraTime);
      }
    };

    socket.connect();
    socket.on("extra_time", handleExtraTime);

    return () => {
      socket.off("extra_time", handleExtraTime);
      socket.disconnect();
    };
  }, [isInitialized]);

  // Apply extra time when received
  useEffect(() => {
    if (isInitialized && extraTime) {
      applyExtraTime();
    }
  }, [extraTime, isInitialized, applyExtraTime]);

  // Countdown timer
  useEffect(() => {
    if (!isInitialized || isTransitioning) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = decrementTime(prev, setIsTimeUp, setIsOnSubmit);
        leftTime = newTime;
        if (newTime === "00:00:00") {
          const subjectCurrentIndex = subjectNames.findIndex(
            (item) => item.subject_id === activeSubjectId
          );
          const isLastSubject = subjectCurrentIndex === subjectNames.length - 1;

          if (isLastSubject) {
            sessionStorage.setItem("f_submit", "yes");
            sessionStorage.setItem("subject_submit", "yes");
            sessionStorage.setItem("subject_timeUp", "yes");
          } else {
            setIsTransitioning(true);
            sessionStorage.setItem("subject_submit", "yes");
            sessionStorage.setItem("subject_timeUp", "yes");
            const nextTime = changeSubjectTimeUtil(activeSubjectId, true);
            leftTime = nextTime;
            sessionStorage.setItem("remainingTime", nextTime);
            return nextTime;
          }
        }

        leftTime = newTime;
        sessionStorage.setItem("remainingTime", newTime);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isInitialized, activeSubjectId, subjectNames, isTransitioning]);

  useEffect(() => {
    if (isInitialized) {
      onInitialized(true);
    }
  }, [isInitialized, onInitialized]);

  return (
    <>
      {isInitialized ? remainingTime : null}
      <FinalSubmissionDialog
        open={showSubmitModal || isTimeUp}
        onClose={handleCloseSubmitModal}
        onSubmit={handleFinalSubmit}
        isTimeUp={isTimeUp}
        isSubjectTimeUp={isSubjectTimeUp}
        stats={stats}
        submitError={submitError}
        countdownTime={countdownTime}
        finalSubmitChecked={finalSubmitChecked}
        onFinalSubmitCheck={handleFinalSubmitCheck}
        nextSubjectCounter={nextSubjectCounter}
        nextSubject={changeSubjectTime}
        isSubjectSubmit={isSubjectSubmit}
      />
    </>
  );
}

export default TimerComponentSubject;
export { changeSubjectTimeUtil as changeSubjectTime };
